import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";

import ImageIcon from "@mui/icons-material/Image";
import ArticleIcon from "@mui/icons-material/Article";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { tokens } from "../../../theme";
import { ReferralProgram } from "../types/view.referral";
import apiService from "../../../service/Api";

// ── Types ──────────────────────────────────────────────────────────────────────

type DocumentType = "agreement" | "identity" | "payment" | "other";

interface ReferralDocument {
  id: number;
  title: string | null;
  file: string;
  file_url: string;
  document_type: DocumentType;
  uploaded_at: string;
  refferal_program_id: number;
}

interface PaginatedResponse<T> {
  results: T[];
  count: number;
}

interface SnackState {
  msg: string;
  severity: "success" | "error";
}

// ── Constants ──────────────────────────────────────────────────────────────────

const DOCUMENT_TYPE_OPTIONS: { value: DocumentType; label: string }[] = [
  { value: "agreement", label: "Agreement" },
  { value: "identity", label: "ID Doc" },
  { value: "payment", label: "Payment" },
  { value: "other", label: "Other" },
];

const API_PATH = "/api/refferal-program/refferal-documents";

// ── Helpers ────────────────────────────────────────────────────────────────────

function getFullUrl(url: string): string {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const base = process.env.REACT_APP_API_URL ?? window.location.origin;
  return `${base}${url}`;
}

function formatBytes(bytes: number): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function extractMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "An unexpected error occurred";
}

function getFileName(doc: ReferralDocument): string {
  if (doc.title) return doc.title;
  const url = doc.file_url || doc.file;
  if (!url) return "—";
  return decodeURIComponent(url.split("/").pop()?.split("?")[0] ?? "—");
}

function getFileExtension(doc: ReferralDocument): string {
  return getFileName(doc).toLowerCase().split(".").pop() ?? "";
}

function isImageDoc(doc: ReferralDocument): boolean {
  return ["jpg", "jpeg", "png", "gif", "webp"].includes(getFileExtension(doc));
}

function FileIcon({ doc }: { doc: ReferralDocument }) {
  const ext = getFileExtension(doc);
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  const isWord = ["doc", "docx"].includes(ext);

  if (isImage) return <ImageIcon sx={{ color: "#4A90D9", flexShrink: 0 }} />;
  if (isWord) return <ArticleIcon sx={{ color: "#2B579A", flexShrink: 0 }} />;
  return <PictureAsPdfIcon sx={{ color: "#E85C57", flexShrink: 0 }} />;
}

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  data: ReferralProgram;
}

export default function DocumentsCard({ data }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // ── State ──────────────────────────────────────────────────────────────────

  const [documents, setDocuments] = useState<ReferralDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<DocumentType>("other");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit (patch) dialog
  const [editDoc, setEditDoc] = useState<ReferralDocument | null>(null);
  const [editType, setEditType] = useState<DocumentType>("other");
  const [patching, setPatching] = useState(false);

  // Preview dialog
  const [previewDoc, setPreviewDoc] = useState<ReferralDocument | null>(null);

  // Delete
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Snackbar
  const [snack, setSnack] = useState<SnackState | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!data?.id) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const res = await apiService.get<PaginatedResponse<ReferralDocument>>(
          `${API_PATH}/?refferal_program=${data.id}`,
        );
        if (!cancelled) {
          setDocuments(res.data.results ?? []);
        }
      } catch (err) {
        if (!cancelled) setFetchError(extractMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data?.id]);

  // ── Download (blob) ────────────────────────────────────────────────────────

  async function handleDownload(doc: ReferralDocument) {
    const url = getFullUrl(doc.file_url);
    try {
      const response = await fetch(url, { credentials: "include" });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = getFileName(doc);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setSnack({ msg: extractMessage(err), severity: "error" });
    }
  }

  // ── Upload ─────────────────────────────────────────────────────────────────

  async function handleUpload() {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("document_type", uploadType);
      formData.append("refferal_program_id", String(data.id));

      const res = await apiService.post<ReferralDocument>(
        `${API_PATH}/`,
        formData,
      );

      setDocuments((prev) => [res.data, ...prev]);
      setSnack({ msg: "Document uploaded successfully", severity: "success" });
      setUploadOpen(false);
      setUploadFile(null);
      setUploadType("other");
    } catch (err) {
      setSnack({ msg: extractMessage(err), severity: "error" });
    } finally {
      setUploading(false);
    }
  }

  // ── Patch ──────────────────────────────────────────────────────────────────

  async function handlePatch() {
    if (!editDoc) return;
    setPatching(true);
    try {
      const res = await apiService.patch<ReferralDocument>(
        `${API_PATH}/${editDoc.id}/`,
        { document_type: editType },
      );
      setDocuments((prev) =>
        prev.map((d) => (d.id === res.data.id ? res.data : d)),
      );
      setSnack({ msg: "Document updated", severity: "success" });
      setEditDoc(null);
    } catch (err) {
      setSnack({ msg: extractMessage(err), severity: "error" });
    } finally {
      setPatching(false);
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  async function handleDelete(id: number) {
    setDeletingId(id);
    try {
      await apiService.delete(`${API_PATH}/${id}/`);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      setSnack({ msg: "Document deleted", severity: "success" });
    } catch (err) {
      setSnack({ msg: extractMessage(err), severity: "error" });
    } finally {
      setDeletingId(null);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "20px",
          p: 2.5,
          backgroundColor: colors.primary[900],
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Documents
          </Typography>
          <IconButton size="small" onClick={() => setUploadOpen(true)}>
            <AddCircleOutlineOutlined fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : fetchError ? (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              color: "#E85C57",
              fontSize: "0.875rem",
            }}
          >
            {fetchError}
          </Box>
        ) : documents.length === 0 ? (
          <Box
            sx={{
              borderRadius: "16px",
              p: 3,
              color: "#8A94A6",
              textAlign: "center",
            }}
          >
            No documents available at the moment
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {documents.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #F0F2F6",
                  borderRadius: "14px",
                  px: 1.5,
                  py: 1.2,
                  backgroundColor: "#FCFCFD",
                }}
              >
                {/* Left: icon + meta */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    minWidth: 0,
                  }}
                >
                  <FileIcon doc={doc} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: "#4A5568",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 180,
                      }}
                    >
                      {getFileName(doc)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#A0AEC0" }}>
                      {DOCUMENT_TYPE_OPTIONS.find(
                        (o) => o.value === doc.document_type,
                      )?.label ?? doc.document_type}{" "}
                      · {formatDate(doc.uploaded_at)}
                    </Typography>
                  </Box>
                </Box>

                {/* Right: actions */}
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                  {/* View */}
                  <IconButton
                    size="small"
                    disabled={!doc.file_url}
                    onClick={() => setPreviewDoc(doc)}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>

                  {/* Download */}
                  <IconButton
                    size="small"
                    disabled={!doc.file_url}
                    onClick={() => handleDownload(doc)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>

                  {/* Delete */}
                  <IconButton
                    size="small"
                    disabled={deletingId === doc.id}
                    onClick={() => handleDelete(doc.id)}
                  >
                    {deletingId === doc.id ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* ── Upload Dialog ──────────────────────────────────────────────────── */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Upload Document</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "12px !important",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            style={{ display: "none" }}
            onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
          />

          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: "2px dashed #CBD5E0",
              borderRadius: "12px",
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              color: "#8A94A6",
              "&:hover": { borderColor: "#A0AEC0" },
            }}
          >
            {uploadFile ? (
              <Typography sx={{ fontSize: "0.875rem", color: "#4A5568" }}>
                {uploadFile.name}{" "}
                <span style={{ color: "#A0AEC0" }}>
                  ({formatBytes(uploadFile.size)})
                </span>
              </Typography>
            ) : (
              <Typography sx={{ fontSize: "0.875rem" }}>
                Click to select a file
              </Typography>
            )}
          </Box>

          <Select
            size="small"
            value={uploadType}
            onChange={(e) => setUploadType(e.target.value as DocumentType)}
            fullWidth
          >
            {DOCUMENT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={14} /> : undefined}
          >
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit (Patch) Dialog ────────────────────────────────────────────── */}
      <Dialog
        open={!!editDoc}
        onClose={() => setEditDoc(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Document</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#8A94A6", mb: 1 }}>
            Document type
          </Typography>
          <Select
            size="small"
            value={editType}
            onChange={(e) => setEditType(e.target.value as DocumentType)}
            fullWidth
          >
            {DOCUMENT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDoc(null)} disabled={patching}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePatch}
            disabled={patching}
            startIcon={patching ? <CircularProgress size={14} /> : undefined}
          >
            {patching ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Preview Dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
            {previewDoc ? getFileName(previewDoc) : ""}
          </Typography>
          <IconButton
            size="small"
            disabled={!previewDoc?.file_url}
            onClick={() => previewDoc && handleDownload(previewDoc)}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: "#f5f5f5", minHeight: 500 }}>
          {previewDoc &&
            (isImageDoc(previewDoc) ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                  minHeight: 500,
                }}
              >
                <img
                  src={getFullUrl(previewDoc.file_url)}
                  alt={getFileName(previewDoc)}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: 8,
                  }}
                />
              </Box>
            ) : (
              <iframe
                src={getFullUrl(previewDoc.file_url)}
                title={getFileName(previewDoc)}
                width="100%"
                height="600px"
                style={{ border: "none", display: "block" }}
              />
            ))}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPreviewDoc(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ───────────────────────────────────────────────────────── */}
      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack?.severity}
          onClose={() => setSnack(null)}
          sx={{ width: "100%" }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
